/* eslint-disable react/no-multi-comp */
import React, { useRef, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { store } from '../../store/index';
import {
  CircularProgress,
  Button,
  IconButton,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

// Actions
import {
  getRoutes,
  getRoute,
  setUpdateRoute,
  setUpdateGeoJson,
  clearUpdateRoute,
  getElevation,
  updateRoute,
  toggleUpdateLineAndPoints,
  showDeleteConfirm,
  toggleEnableRoute,
  setUpdateRouteStartPointElevation,
  setUpdateRouteStartPoint,
  setUpdateRouteRelAlt
} from 'actions/routePlannerActions';

// Dumb Components
import JsonViewer from './components/JsonViewer';
import ElevationGraph from './components/ElevationGraph';
import VirtualRouteList from './components/VirtualRouteList';
import HighwayDropdown from './components/HighwayDropdown';

// Smart Components
import QRCodeDialog from './QRCodeDialog';

// Hooks
import { useElevationDataForRoute } from 'hooks/routePlannerHooks';
import { newNotification } from 'actions/uiActions';
import { SET_DROP_LOCATION } from 'actions/actionTypes';
import { zoneColourIcon } from 'utils/ui';

// Dynamic height and width of virtual route list
const routeListHeight = Math.round(window.innerHeight * 0.82);
const routeListWidth = Math.round(window.innerWidth * 0.405);

const RouteList = props => {
  const {
    updateRoute,
    updateShowLine,
    setUpdateGeoJson,
    setUpdateRoute,
    sendUpdatedRoute,
    showDeleteConfirm,
    toggleUpateLineAndPoints,
    clearUpdateRoute,
    highwayKeys,
    toggleEnableRoute,
    fetchingRoutes,
    updatingRoute,
    updateRouteGeoJson,
    routes,
    routesKeys,
    deletingRoute,
    enablingRoute,
    updateRouteStartPointElevation,
    setUpdateRouteStartPoint,
    setUpdateRouteStartPointElevation,
    setUpdateRouteRelAlt,
    updateRouteRelAlt,
    notify,
    urlRoute,
    setDropLocation
  } = props;

  const [elevationGraphDimensions, setElevationGraphDimensions] = useState();
  const [searchResults, setSearchResults] = useState(routesKeys);
  const [routeSearchQuery, setRouteQuery] = useState('');
  const [updateHighwayKey, setUpdateHighwayKey] = useState();
  const [openQRCode, setOpenQRCode] = useState(false);

  const containerRef = useRef();
  const [graphLines] = useElevationDataForRoute(
    updateRoute?.misc1,
    updateRouteStartPointElevation
  );

  useEffect(() => {
    if (!fetchingRoutes && routes && urlRoute) {
      onSelectRoute(routes[urlRoute]);
    }
  }, [fetchingRoutes, routes, urlRoute]);

  useEffect(() => {
    if (updateRoute) {
      if (updateShowLine) {
        showEditButton(true);
        setUpdateGeoJson(JSON.stringify(updateRoute.misc2, null, 2));
      } else {
        showEditButton(false);
        setUpdateGeoJson(JSON.stringify(updateRoute.misc1, null, 2));
      }
    }
  }, [updateShowLine]);

  useEffect(() => {
    const width = window.innerWidth * 0.38;
    const height = window.innerHeight * 0.28;
    setElevationGraphDimensions({ width, height });
  }, []);

  useEffect(() => {
    if (routesKeys) {
      if (routeSearchQuery.trim() !== '') {
        const foundRouteKeys = routesKeys.filter(routeKey =>
          routeKey ? routeKey.includes(routeSearchQuery) : false
        );
        setSearchResults(foundRouteKeys);
      } else {
        setSearchResults(routesKeys);
      }
    }
  }, [routeSearchQuery, routesKeys]);

  const showEditButton = show => {
    const editBtn = document.getElementsByClassName(
      'leaflet-draw-edit-edit'
    )[0];
    if (editBtn) {
      editBtn.style.display = show ? 'block' : 'none';
    }
  };

  const createLineStringWithEircodeLocation = (lat, lng) => {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [[parseFloat(lng), parseFloat(lat)]]
          }
        }
      ]
    };
  };

  const createPointsCollectionWithEircodeLocation = (lat, lng) => {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            elevation: '133',
            xdelta: '0',
            index: '0',
            type: 'startpoint',
            relativeAltitude: '45',
            speed: '16'
          },
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(lng), parseFloat(lat)]
          }
        }
      ]
    };
  };

  const onSelectUpdateHighway = highwayKey => {
    setUpdateHighwayKey(highwayKey);
    const updateHighway = routes[highwayKey];

    getRoute(
      updateHighway.id,
      res => {
        const routeToUpdate = updateRoute;

        const mergedPointsCollectionEircodeAndHighway = [
          ...res.misc1.features,
          updateRoute.misc1.features[0]
        ];

        const mergedLineStringEircodeAndHighway = [
          ...res.misc2.features[0].geometry.coordinates,
          updateRoute.misc2.features[0].geometry.coordinates[0]
        ];

        routeToUpdate.misc1.features = mergedPointsCollectionEircodeAndHighway;
        routeToUpdate.misc2.features[0].geometry.coordinates = mergedLineStringEircodeAndHighway;

        setUpdateGeoJson(JSON.stringify(routeToUpdate.misc2, null, 2));
        setUpdateRoute(routeToUpdate);

        if (res.dropLocation) {
          setDropLocation(res.dropLocation);
        }

        const startPoint = {
          lat: updateRoute.misc2.features[0].geometry.coordinates[0][1],
          lng: updateRoute.misc2.features[0].geometry.coordinates[0][0]
        };

        setUpdateRouteStartPoint(startPoint);
        getElevation(`${startPoint.lat},${startPoint.lng}`)
          .then(res => {
            setUpdateRouteStartPointElevation(res.data.results[0].elevation);
          })
          .catch(e => {
            console.log(e);
          });
      },
      err => {
        console.log(err);
        notify({ variant: 'error', message: 'Failed to fetch highway route' });
      }
    );
  };

  const onSelectRoute = route => {
    getRoute(
      route.id,
      res => {
        if (res.misc2 && res.misc1) {
          setUpdateRoute(res);
          setUpdateGeoJson(JSON.stringify(res.misc2, null, 2));
        } else {
          const updateRouteLineStringWithEircodeLocation = createLineStringWithEircodeLocation(
            route.address_latitude,
            route.address_longitude
          );
          const updateRoutePointsCollectionWithEircodeLocation = createPointsCollectionWithEircodeLocation(
            route.address_latitude,
            route.address_longitude
          );
          const routeWithEircodePoint = route;
          routeWithEircodePoint.misc1 = updateRoutePointsCollectionWithEircodeLocation;
          routeWithEircodePoint.misc2 = updateRouteLineStringWithEircodeLocation;

          setUpdateGeoJson(
            JSON.stringify(updateRouteLineStringWithEircodeLocation, null, 2)
          );
          setUpdateRoute(routeWithEircodePoint);
        }
        if (res.dropLocation) {
          setDropLocation(res.dropLocation);
        }
      },
      () => {
        notify({ variant: 'error', message: 'Failed to fetch slot' });
      }
    );
  };

  const onDeleteRoute = (eircode, id) => {
    showDeleteConfirm(eircode, id);
  };

  const goBack = () => {
    setUpdateHighwayKey(null);
    clearUpdateRoute();
    toggleUpateLineAndPoints(true);
  };

  const onChangeJson = json => {
    setUpdateGeoJson(json);
  };

  const onClickUpdate = () => {
    const newLineStringGeoJson = store.getState().routePlanner
      .updateRouteGeoJson;
    const updatedRouteObject = {
      ...updateRoute,
      misc1: JSON.stringify(updateRoute.misc1),
      misc2: newLineStringGeoJson
    };
    sendUpdatedRoute(updatedRouteObject, updateRouteRelAlt);
  };

  const onClickDelete = () => {
    showDeleteConfirm(updateRoute.eircode, updateRoute.id);
  };

  const onClickEnable = () => {
    toggleEnableRoute(updateRoute);
  };

  const onClickToggleLine = () => {
    toggleUpateLineAndPoints(!updateShowLine);
  };

  const onChangeRouteQuery = evt => {
    setRouteQuery(evt.target.value.toUpperCase());
  };

  const onChangeRelAlt = (_evt, value) => {
    setUpdateRouteRelAlt(value);
  };

  console.log('graphLines: ', graphLines);

  if (!updateRoute) {
    return (
      <div className="w-full h-full max-w-full" ref={containerRef}>
        <TextField
          value={routeSearchQuery}
          placeholder="Search"
          fullWidth
          className="p-3"
          onChange={onChangeRouteQuery}
        />
        {routes && searchResults && (
          <VirtualRouteList
            routes={routes}
            routeKeys={searchResults}
            rowHeight={50}
            onSelectRoute={onSelectRoute}
            onDeleteRoute={onDeleteRoute}
            width={routeListWidth}
            height={routeListHeight}
          />
        )}
        {fetchingRoutes && (
          <div className="text-center w-full pt-24">
            <CircularProgress />
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div>
        <div className="border-b border-gray-200">
          <Grid container justify="space-between">
            <Grid item>
              <IconButton aria-label="delete" onClick={goBack}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" className="inline-block mt-3">
                {zoneColourIcon(updateRoute.zone)}{' '}
                {updateRoute && updateRoute.eircode} ({updateRoute.km_distance}
                km)
              </Typography>
              {updateRoute.devRouteName && (
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  className="m-2.5"
                  onClick={() => setOpenQRCode(true)}>
                  QR Code
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant="contained"
                color="primary"
                className="m-1.25"
                onClick={onClickEnable}
                disabled={enablingRoute}>
                {enablingRoute ? <CircularProgress size={10} /> : null}
                {updateRoute.enabled ? 'Disable' : 'Enable'}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                className="m-1.25"
                onClick={onClickUpdate}
                disabled={!updateShowLine || updatingRoute}>
                {updatingRoute ? <CircularProgress size={10} /> : null}
                {updatingRoute ? 'Updating' : 'Update'}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                className="m-1.25"
                onClick={onClickDelete}
                disabled={deletingRoute}>
                {deletingRoute ? <CircularProgress size={10} /> : null}
                {deletingRoute ? 'Deleting' : 'Delete'}
              </Button>
            </Grid>
          </Grid>
        </div>
        <Grid container spacing={2} className="p-4">
          <Grid item xs={9}>
            <HighwayDropdown
              highwayKeys={highwayKeys}
              onChange={onSelectUpdateHighway}
              value={updateHighwayKey}
            />
          </Grid>
          <Grid item xs={3}>
            <FormControlLabel
              className="mt-3.75"
              control={
                <Checkbox
                  checked={updateRouteRelAlt}
                  onChange={onChangeRelAlt}
                  name="checkedB"
                  color="primary"
                />
              }
              label="Rel Alt"
            />
          </Grid>
        </Grid>
        <div className={graphLines ? "relative h-1/2 overflow-y-scroll" : "relative h-full overflow-y-scroll"}>
          {updateRoute && (
            <Button
              size="small"
              variant="contained"
              className="absolute top-5 right-6 z-50"
              onClick={onClickToggleLine}>
              {updateShowLine ? 'Show Points' : 'Show LineString'}
            </Button>
          )}
          <JsonViewer
            value={updateRouteGeoJson}
            onChangeJson={onChangeJson}
            readOnly={!updateShowLine}
          />
        </div>
        {graphLines && (
          <div className="p-5 relative">
            <ElevationGraph
              groundElevation={graphLines.groundGraphLine}
              dronePlannedAltitude={graphLines.droneGraphLine}
              height={elevationGraphDimensions.height}
              width={elevationGraphDimensions.width}
            />
          </div>
        )}
        {openQRCode && (
          <QRCodeDialog
            open={openQRCode}
            routeEircode={updateRoute.eircode}
            handleClose={() => setOpenQRCode(false)}
          />
        )}
      </div>
    );
  }
};

const mapStateToProps = state => ({
  routes: state.routePlanner.routes,
  routesKeys: state.routePlanner.routesKeys,
  highwayKeys: state.routePlanner.highwayKeys,
  fetchingRoutes: state.routePlanner.fetchingRoutes,
  updateRoute: state.routePlanner.updateRoute,
  updateRouteGeoJson: state.routePlanner.updateRouteGeoJson,
  updateShowLine: state.routePlanner.updateShowLine,
  updatingRoute: state.routePlanner.updatingRoute,
  deletingRoute: state.routePlanner.deletingRoute,
  updateRouteStartPointElevation:
    state.routePlanner.updateRouteStartPointElevation,
  enablingRoute: state.routePlanner.enablingRoute,
  updateRouteRelAlt: state.routePlanner.updateRouteRelAlt
});

const mapDispatchToProps = dispatch => ({
  notify: notification => dispatch(newNotification(notification)),
  setUpdateRouteRelAlt: relAlt => dispatch(setUpdateRouteRelAlt(relAlt)),
  getRoutes: () => dispatch(getRoutes()),
  setUpdateGeoJson: gJson => dispatch(setUpdateGeoJson(gJson)),
  setUpdateRoute: route => dispatch(setUpdateRoute(route)),
  setUpdateRouteStartPoint: startPoint =>
    dispatch(setUpdateRouteStartPoint(startPoint)),
  setUpdateRouteStartPointElevation: el =>
    dispatch(setUpdateRouteStartPointElevation(el)),
  sendUpdatedRoute: (updatedRouteObject, relAlt) =>
    dispatch(updateRoute(updatedRouteObject, relAlt)),
  clearUpdateRoute: () => dispatch(clearUpdateRoute()),
  toggleUpateLineAndPoints: showLine =>
    dispatch(toggleUpdateLineAndPoints(showLine)),
  showDeleteConfirm: (eircode, id) => dispatch(showDeleteConfirm(eircode, id)),
  toggleEnableRoute: route => dispatch(toggleEnableRoute(route)),
  setDropLocation: dropLocation =>
    dispatch({ type: SET_DROP_LOCATION, payload: dropLocation })
});

export default connect(mapStateToProps, mapDispatchToProps)(RouteList);
